import React, { useState, useEffect } from 'react';
import { FaSyncAlt, FaClock, FaPlay, FaStop, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import { Button, Card, StatusIndicator } from '../../design-system/components';
import { cn } from '../../design-system/utils/cn';
import { useAuth } from '../../context/AuthContext';
const SystemSettings = () => {
  const [serviceStatuses, setServiceStatuses] = useState({});
  const [scheduledRestarts, setScheduledRestarts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { apiRequest } = useAuth();

  const services = [
    { id: 'zigbee2mqtt', name: 'Zigbee2MQTT', description: 'Zigbee device communication' },
    { id: 'frigate', name: 'Frigate NVR', description: 'Camera recording and detection' },
    { id: 'slzb-06', name: 'SLZB-06 Adapter', description: 'Zigbee coordinator adapter' },
    { id: 'mqtt', name: 'MQTT Broker', description: 'Message broker for IoT communication' },
    { id: 'caddy', name: 'Caddy Server', description: 'Web server and reverse proxy' }
  ];

  const scheduleOptions = [
    { value: 'daily', label: 'Daily at 3 AM' },
    { value: 'weekly', label: 'Weekly (Sunday 3 AM)' },
    { value: 'monthly', label: 'Monthly (1st at 3 AM)' }
  ];

  useEffect(() => {
    fetchServiceStatuses();
    fetchScheduledRestarts();
  }, []);

  const fetchServiceStatuses = async () => {
    try {
      const {response,data} = await apiRequest('/api/system/services/status');
      
      if (data.success) {
        setServiceStatuses(data.services);
      }
    } catch (error) {
      console.error('Failed to fetch service statuses:', error);
    }
  };

  const fetchScheduledRestarts = async () => {
    try {
      const {response,data}  = await apiRequest('/api/system/services/schedule');
      // const data = await response.json();
      if (data.success) {
        setScheduledRestarts(data.schedules);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled restarts:', error);
    }
  };

  const restartService = async (serviceId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {response,data}  = await apiRequest(`/api/system/services/${serviceId}/restart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // const data = await response.json();
      
      if (data.success) {
        setSuccess(`${serviceId} restarted successfully`);
        setTimeout(() => fetchServiceStatuses(), 2000);
      } else {
        setError(data.message || 'Failed to restart service');
      }
    } catch (error) {
      setError(`Failed to restart ${serviceId}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const scheduleRestart = async (serviceId, schedule) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {response,data}  = await apiRequest('/api/system/services/schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service: serviceId,
          schedule: schedule
        })
      });

      // const data = await response.json();
      
      if (data.success) {
        setSuccess(`Periodic restart scheduled for ${serviceId}`);
        fetchScheduledRestarts();
      } else {
        setError(data.message || 'Failed to schedule restart');
      }
    } catch (error) {
      setError(`Failed to schedule restart: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeScheduledRestart = async (serviceId) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const {response,data}  = await apiRequest(`/api/system/services/${serviceId}/schedule`, {
        method: 'DELETE'
      });

      // const data = await response.json();
      
      if (data.success) {
        setSuccess(`Scheduled restart removed for ${serviceId}`);
        fetchScheduledRestarts();
      } else {
        setError(data.message || 'Failed to remove scheduled restart');
      }
    } catch (error) {
      setError(`Failed to remove scheduled restart: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <FaPlay className="text-secondary-500" />;
      case 'stopped':
        return <FaStop className="text-error-500" />;
      default:
        return <FaExclamationTriangle className="text-warning-500" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <div className="p-5 max-w-7xl mx-auto space-y-8">
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Service Management</h2>
          <p className="text-gray-600">Monitor and restart system services</p>
        </div>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-error-50 border border-error-200 rounded-xl text-error-700">
            <FaExclamationTriangle className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-3 p-4 bg-secondary-50 border border-secondary-200 rounded-xl text-secondary-700">
            <span>{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {services.map(service => {
            const status = serviceStatuses[service.id];
            const isScheduled = scheduledRestarts.some(s => s.service === service.id);
            
            return (
              <Card key={service.id} size="lg" className="transition-all duration-200">
                <div className="flex justify-between items-start mb-6">
                  <div className="space-y-1">
                    <h3 className="font-semibold text-gray-900">{service.name}</h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </div>
                  <StatusIndicator
                    status={status?.status || 'unknown'}
                    text={status?.status || 'unknown'}
                    icon={getStatusIcon(status?.status)}
                    variant={getStatusVariant(status?.status)}
                    size="sm"
                    showDot={false}
                  />
                </div>

                <div className="space-y-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="primary"
                    fullWidth
                    leftIcon={<FaSyncAlt />}
                    onClick={() => restartService(service.id)}
                    loading={loading}
                  >
                    Restart Now
                  </Button>

                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Periodic Restart</h4>
                    {isScheduled ? (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaClock className="text-primary-500" />
                          <span>Scheduled</span>
                        </div>
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<FaTrash />}
                          onClick={() => removeScheduledRestart(service.id)}
                          loading={loading}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <select
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        onChange={(e) => {
                          if (e.target.value) {
                            scheduleRestart(service.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        disabled={loading}
                      >
                        <option value="">Select schedule...</option>
                        {scheduleOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">Scheduled Restarts</h2>
          <p className="text-gray-600">View and manage periodic service restarts</p>
        </div>

        {scheduledRestarts.length === 0 ? (
          <Card size="xl">
            <div className="text-center py-12">
              <FaClock className="mx-auto text-4xl text-gray-300 mb-4" />
              <p className="text-gray-500">No scheduled restarts configured</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {scheduledRestarts.map((schedule, index) => (
              <Card key={index} size="lg">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="font-medium text-gray-900 capitalize">{schedule.service}</h4>
                    <p className="text-sm text-gray-600">Schedule: {schedule.cronExpression}</p>
                    {schedule.comment && (
                      <p className="text-sm text-gray-500 italic">{schedule.comment}</p>
                    )}
                  </div>
                  <Button
                    variant="danger"
                    leftIcon={<FaTrash />}
                    onClick={() => removeScheduledRestart(schedule.service)}
                    loading={loading}
                    className="md:self-start"
                  >
                    Remove
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettings;